import { cloneDeep } from "lodash-es";

import type { RevMetaType, RevType } from "../../shared/types/sharedb";
import type { WorksheetType } from "../../shared/types/worksheet";
import { jsonOTType } from "../../shared/worksheets/ot-types/ot-patch";

import { type PageEvent, PageEventType } from "components/server-types";

export const MINIMUM_IDLE_TIME_MS = 120_000;
export const MAX_VISIBLE_TIMELINE_VERSIONS = 50;

export type TimelineMarkKind = "first" | "day" | "publication" | "submission_collection";

export const SCRUBBER_MARK_KINDS: TimelineMarkKind[] = [
  "day",
  "publication",
  "submission_collection",
];

export const snapshotHasScrubberMark = (marks: TimelineMarkKind[]): boolean =>
  marks.some((mark) => SCRUBBER_MARK_KINDS.includes(mark));

export type WorksheetSnapshot = {
  doc: WorksheetType;
  meta: RevMetaType;
  marks: TimelineMarkKind[];
  userIds: string[];
  eventType?: PageEventType;
  eventBy?: string | null;
};

type EditPoint = {
  ts: number;
  userIds: string[];
  eventType?: PageEventType;
  eventBy?: string | null;
};

const applyEdit = (doc: WorksheetType, edit: RevType): WorksheetType => {
  if (!edit.op) {
    return doc;
  }

  return jsonOTType.type.apply(cloneDeep(doc), edit.op) as WorksheetType;
};

const eventTimeMs = (createdAt: string) => new Date(createdAt).getTime();

const collectIdleEditPoints = (points: { ts: number; userId: string }[]): EditPoint[] => {
  if (points.length === 0) {
    return [];
  }

  const editPoints: EditPoint[] = [];
  let sessionUserIds: Set<string> = new Set();
  points.forEach((point, index) => {
    sessionUserIds.add(point.userId);

    const isLast = index === points.length - 1;
    const gapToNext = isLast ? Number.POSITIVE_INFINITY : points[index + 1].ts - point.ts;
    if (gapToNext >= MINIMUM_IDLE_TIME_MS) {
      editPoints.push({
        ts: point.ts,
        userIds: Array.from(sessionUserIds),
        eventType: PageEventType.WORKSHEET_CHANGE,
      });

      sessionUserIds = new Set();
    }
  });

  return editPoints;
};

const mergeEditPoints = (idleEditPoints: EditPoint[], events: PageEvent[]): EditPoint[] => {
  const byTs = new Map<number, EditPoint>();
  for (const editPoint of idleEditPoints) {
    byTs.set(editPoint.ts, { ...editPoint });
  }

  for (const event of events) {
    const ts = eventTimeMs(event.created_at);
    const existing = byTs.get(ts);
    if (existing) {
      existing.eventType = event.event_type;
      existing.eventBy = event.event_by;
    } else {
      byTs.set(ts, {
        ts,
        userIds: [],
        eventType: event.event_type,
        eventBy: event.event_by,
      });
    }
  }

  return [...byTs.values()].sort((left, right) => left.ts - right.ts);
};

const capVersions = (versions: WorksheetSnapshot[]): WorksheetSnapshot[] => {
  if (versions.length <= MAX_VISIBLE_TIMELINE_VERSIONS) {
    return versions;
  }

  return [versions[0], ...versions.slice(1).slice(-(MAX_VISIBLE_TIMELINE_VERSIONS - 1))];
};

const withMarks = (versions: WorksheetSnapshot[]): WorksheetSnapshot[] => {
  let previousDay: number | null = null;
  return versions.map((version, index) => {
    const marks: TimelineMarkKind[] = [];
    if (index === 0) {
      marks.push("first");
    }

    const day = Math.floor(version.meta.ts / (1000 * 60 * 60 * 24));
    if (day !== previousDay) {
      previousDay = day;
      marks.push("day");
    }

    if (version.eventType === PageEventType.PUBLICATION) {
      marks.push("publication");
    }

    if (version.eventType === PageEventType.SUBMISSION_COLLECTION) {
      marks.push("submission_collection");
    }

    return { ...version, marks };
  });
};

/** One snapshot per ShareDB op. Used by the debug timeline (`debug=1`). */
export const buildSnapshots = (
  createRevision: RevType,
  editRevisions: RevType[],
): WorksheetSnapshot[] => {
  const createData = createRevision.create?.data;
  if (!createData) {
    return [];
  }

  return withMarks(
    editRevisions.reduce(
      (snapshots, editRevision) => {
        const previousDoc = snapshots[snapshots.length - 1].doc;
        return [
          ...snapshots,
          {
            doc: applyEdit(previousDoc, editRevision),
            meta: editRevision.m,
            marks: [],
            userIds: [editRevision.m.userId],
          },
        ];
      },
      [
        {
          doc: createData,
          meta: createRevision.m,
          marks: [],
          userIds: [createRevision.m.userId],
        } satisfies WorksheetSnapshot,
      ],
    ),
  );
};

/**
 * The create snapshot is always its own version. Later idle-session versions
 * (120s with no ShareDB edits) plus page-event versions. Edit versions are
 * never created mid-session. Caps at 50, always keeping the create snapshot.
 */
export const buildIdleSnapshots = (
  createRevision: RevType,
  editRevisions: RevType[],
  pageEvents: PageEvent[] = [],
): WorksheetSnapshot[] => {
  const createData = createRevision.create?.data;
  if (!createData) {
    return [];
  }

  const createPoint: EditPoint = {
    ts: createRevision.m.ts,
    userIds: createRevision.m.userId ? [createRevision.m.userId] : [],
    eventType: PageEventType.WORKSHEET_CHANGE,
  };

  const idleEditPoints = collectIdleEditPoints(
    editRevisions.map((edit) => ({
      ts: edit.m.ts,
      userId: edit.m.userId,
    })),
  );
  const editPoints = mergeEditPoints([createPoint, ...idleEditPoints], pageEvents);

  if (editPoints.length === 0) {
    return [];
  }

  let doc = createData;
  let editIndex = 0;
  const versions: WorksheetSnapshot[] = [];

  for (const editPoint of editPoints) {
    while (editIndex < editRevisions.length && editRevisions[editIndex].m.ts <= editPoint.ts) {
      doc = applyEdit(doc, editRevisions[editIndex]);
      editIndex += 1;
    }

    versions.push({
      doc: cloneDeep(doc),
      meta: { ts: editPoint.ts, userId: editPoint.userIds[0] ?? "" },
      marks: [],
      userIds: editPoint.userIds,
      eventType: editPoint.eventType,
      eventBy: editPoint.eventBy,
    });
  }

  if (versions.length === 0) {
    return [];
  }

  return withMarks(capVersions(versions));
};
